import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Layout, FloatButton } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css';
import './App.css';

// Components
import AppHeader from './components/AppHeader';
import AppSidebar from './components/AppSidebar';
import ChatPanel from './components/ChatPanel';
import { ProtectedRoute } from './components/PermissionGate';

// Contexts
import { PermissionProvider } from './contexts/PermissionContext';

// Pages
import Dashboard from './pages/Dashboard';
import Vendors from './pages/Vendors';
import VendorDetail from './pages/VendorDetail';
import Products from './pages/Products';
import ScrapedProducts from './pages/ScrapedProducts';
import Recommendations from './pages/Recommendations';
import ConsolidationDetail from './pages/ConsolidationDetail';
import ClientVendors from './pages/ClientVendors';
import Alternatives from './pages/Alternatives';
import VendorComparison from './pages/VendorComparison';
import SpendAnalytics from './pages/SpendAnalytics';
import SpendImport from './pages/SpendImport';
import DataCleansing from './pages/DataCleansing';
import Contracts from './pages/Contracts';
import Renewals from './pages/Renewals';
import VendorScorecards from './pages/VendorScorecards';
import Reports from './pages/Reports';
import SpendForecasting from './pages/SpendForecasting';
import MarketIntelligence from './pages/MarketIntelligence';
import SupplierDiversity from './pages/SupplierDiversity';
import Tasks from './pages/Tasks';
import Notifications from './pages/Notifications';
import VendorMapping from './pages/VendorMapping';
import SavingsOpportunities from './pages/SavingsOpportunities';
import RFPProjects from './pages/RFPProjects';
import RFPProjectDetail from './pages/RFPProjectDetail';
import RFPVendorResponse from './pages/RFPVendorResponse';
import PurchaseOrders from './pages/PurchaseOrders';
import Invoices from './pages/Invoices';
import InvoiceDetail from './pages/InvoiceDetail';
import CostCenters from './pages/CostCenters';
import UserGuide from './pages/UserGuide';
import Login from './pages/Login';
import NotFound from './pages/NotFound';

// Admin Pages
import UserManagement from './pages/UserManagement';
import Departments from './pages/Departments';
import Approvals from './pages/Approvals';
import OrgSettings from './pages/OrgSettings';
import SecuritySettings from './pages/SecuritySettings';
import AuditLogs from './pages/AuditLogs';

// Services
import { approvalService } from './services/api';

const { Content } = Layout;

function App() {
  const [chatVisible, setChatVisible] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);

  // Fetch pending approvals count for sidebar badge
  useEffect(() => {
    const fetchPendingCount = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const stats = await approvalService.getStats();
        setPendingApprovals(stats.pending || 0);
      } catch (error) {
        // Silently fail - user may not have permission
      }
    };

    fetchPendingCount();
    // Refresh every 5 minutes
    const interval = setInterval(fetchPendingCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={
          <PermissionProvider>
            <Layout style={{ minHeight: '100vh' }}>
              <AppHeader />
              <Layout>
                <AppSidebar pendingApprovals={pendingApprovals} />
                <Layout style={{ padding: '24px' }}>
                  <Content style={{ background: '#fff', padding: 24, margin: 0, minHeight: 280 }}>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/my-vendors" element={<ClientVendors />} />
                      <Route path="/vendors" element={<Vendors />} />
                      <Route path="/vendors/:id" element={<VendorDetail />} />
                      <Route path="/products" element={<Products />} />
                      <Route path="/product-database" element={<ScrapedProducts />} />
                      <Route path="/recommendations" element={<Recommendations />} />
                      <Route path="/alternatives" element={<Alternatives />} />
                      <Route path="/compare" element={<VendorComparison />} />
                      <Route path="/spend" element={<SpendAnalytics />} />
                      <Route path="/spend-import" element={<SpendImport />} />
                      <Route path="/data-cleansing" element={<DataCleansing />} />
                      <Route path="/vendor-mapping" element={
                        <ProtectedRoute resource="vendor_mapping" action="view">
                          <VendorMapping />
                        </ProtectedRoute>
                      } />
                      <Route path="/contracts" element={<Contracts />} />
                      <Route path="/renewals" element={<Renewals />} />
                      <Route path="/scorecards" element={<VendorScorecards />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="/forecasting" element={<SpendForecasting />} />
                      <Route path="/market-intelligence" element={<MarketIntelligence />} />
                      <Route path="/supplier-diversity" element={<SupplierDiversity />} />
                      <Route path="/tasks" element={<Tasks />} />
                      <Route path="/notifications" element={<Notifications />} />
                      <Route path="/savings" element={<SavingsOpportunities />} />
                      <Route path="/rfp" element={<RFPProjects />} />
                      <Route path="/rfp/:id" element={<RFPProjectDetail />} />
                      <Route path="/rfp/respond/:token" element={<RFPVendorResponse />} />
                      <Route path="/cost-centers" element={
                        <ProtectedRoute resource="spend" action="view">
                          <CostCenters />
                        </ProtectedRoute>
                      } />
                      <Route path="/consolidation/:vendor1Id/:vendor2Id" element={<ConsolidationDetail />} />

                      {/* Phase 5.1: Accounts Payable Routes */}
                      <Route path="/purchase-orders" element={
                        <ProtectedRoute resource="invoices" action="view">
                          <PurchaseOrders />
                        </ProtectedRoute>
                      } />
                      <Route path="/invoices" element={
                        <ProtectedRoute resource="invoices" action="view">
                          <Invoices />
                        </ProtectedRoute>
                      } />
                      <Route path="/invoices/:id" element={
                        <ProtectedRoute resource="invoices" action="view">
                          <InvoiceDetail />
                        </ProtectedRoute>
                      } />

                      {/* Admin Routes */}
                      <Route path="/users" element={
                        <ProtectedRoute resource="users" action="view">
                          <UserManagement />
                        </ProtectedRoute>
                      } />
                      <Route path="/departments" element={
                        <ProtectedRoute resource="departments" action="view">
                          <Departments />
                        </ProtectedRoute>
                      } />
                      <Route path="/approvals" element={
                        <ProtectedRoute resource="approvals" action="view">
                          <Approvals />
                        </ProtectedRoute>
                      } />
                      <Route path="/settings" element={
                        <ProtectedRoute resource="organization" action="view">
                          <OrgSettings />
                        </ProtectedRoute>
                      } />
                      <Route path="/security" element={<SecuritySettings />} />
                      <Route path="/user-guide" element={<UserGuide />} />
                      <Route path="/audit" element={
                        <ProtectedRoute resource="audit" action="view">
                          <AuditLogs />
                        </ProtectedRoute>
                      } />

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Content>
                </Layout>
              </Layout>

              {/* AI Chat Assistant */}
              <FloatButton
                icon={<MessageOutlined />}
                type="primary"
                style={{ right: 24, bottom: 24, width: 48, height: 48 }}
                onClick={() => setChatVisible(true)}
                tooltip="Ask AI Assistant"
              />
              <ChatPanel visible={chatVisible} onClose={() => setChatVisible(false)} />
            </Layout>
          </PermissionProvider>
        } />
      </Routes>
    </Router>
  );
}

export default App;